import { useCallback, useEffect, useMemo, useRef } from 'react';

const normalizeListQueryParams = (filters = {}, page = 1) => {
  const normalizedFilters = Object.keys(filters || {})
    .sort()
    .reduce((acc, key) => {
      const value = filters[key];

      if (value === undefined || value === null || value === '') {
        return acc;
      }

      acc[key] = value;
      return acc;
    }, {});

  return {
    ...normalizedFilters,
    page: page ?? 1
  };
};

const buildListQueryKey = (filters = {}, page = 1) => JSON.stringify(normalizeListQueryParams(filters, page));

const useListQuery = ({
  store,
  fetchAction,
  filters = {},
  page = 1,
  debounceMs = 300,
  enabled = true
}) => {
  if (!store) {
    throw new Error('useListQuery requires a zustand store hook.');
  }

  const fetchList = store((state) => state[fetchAction]);
  const lastParamsKey = store((state) => state.lastParamsKey || '');
  const lastParams = store((state) => state.lastParams || {});
  const loading = store((state) => Boolean(state.loading));

  const queryParams = useMemo(() => normalizeListQueryParams(filters, page), [filters, page]);
  const queryKey = useMemo(() => buildListQueryKey(filters, page), [filters, page]);

  const fetchRef = useRef(fetchList);
  const paramsRef = useRef(queryParams);

  useEffect(() => {
    fetchRef.current = fetchList;
  }, [fetchList]);

  useEffect(() => {
    paramsRef.current = queryParams;
  }, [queryParams]);

  const refresh = useCallback((overrides = {}) => {
    const nextParams = { ...paramsRef.current, ...overrides };
    return fetchRef.current(nextParams);
  }, []);

  useEffect(() => {
    if (!enabled) return undefined;
    if (lastParamsKey === queryKey) return undefined;

    const timer = window.setTimeout(() => {
      void fetchRef.current(paramsRef.current);
    }, debounceMs);

    return () => window.clearTimeout(timer);
  }, [enabled, debounceMs, lastParamsKey, queryKey]);

  return {
    refresh,
    queryParams,
    queryKey,
    lastParams,
    lastParamsKey,
    isSynced: lastParamsKey === queryKey,
    loading
  };
};

export { buildListQueryKey, normalizeListQueryParams };
export default useListQuery;
